import React, { useState, useEffect } from 'react';
import { CourseService } from '../services/api';
import Modal from './common/Modal';
import Swal from 'sweetalert2';
import { useSettings } from '../context/SettingsContext';
import { getTerminology } from '../utils/terminology';
import { exportToExcel } from '../utils/exportHelper';

const CourseList = () => {
  const { settings } = useSettings();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCourseModal, setShowCourseModal] = useState(false);

  const user = JSON.parse(localStorage.getItem('user'));
  const userRole = user?.role || user?.data?.role;
  const isAdmin = userRole === 'admin' || userRole === 'super_admin';
  const canExport = isAdmin;
  const staffPerms = user?.staff?.permissions || user?.data?.staff?.permissions || [];
  const canManageCourses = isAdmin || staffPerms.includes('manage_courses');
  
  const terminology = React.useMemo(() => {
    return getTerminology(settings.school_type);
  }, [settings.school_type]);

  const isSchool = settings.school_type === 'school';
  const term = terminology.course;
  const terms = terminology.courses;
  const batchTerm = terminology.batch;
  const batchesTerm = terminology.batches;

  // Helper to get currency symbol
  const getCurrencySymbol = (code) => {
    switch (code) {
      case 'PKR': return 'Rs. ';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'INR': return '₹';
      case 'AED': return 'AED ';
      case 'USD': default: return '$';
    }
  };

  const [showBatchModal, setShowBatchModal] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState(null);

  const [courseForm, setCourseForm] = useState({
    name: '',
    code: '',
    description: '',
    fee: '',
    duration_months: ''
  });

  const [batchForm, setBatchForm] = useState({
    name: '',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    course_id: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [editingBatchId, setEditingBatchId] = useState(null);
  const [batchErrors, setBatchErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      const response = await CourseService.getAll();
      setCourses(response.data);
    } catch (error) {
      console.error("Error loading courses", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (courses.length === 0) {
      Swal.fire('No Data', `No ${terms.toLowerCase()} to export.`, 'info');
      return;
    }

    const exportData = [];
    courses.forEach(course => {
      if (course.batches && course.batches.length > 0) {
        course.batches.forEach(batch => {
          exportData.push({
            [term]: course.name,
            'Code': course.code,
            'Monthly Fee': course.fee,
            'Duration': course.duration_months,
            [batchTerm]: batch.name,
            'Timing': batch.timing,
            'Start Date': batch.start_date,
            'End Date': batch.end_date
          });
        });
      } else {
        exportData.push({
          [term]: course.name,
          'Code': course.code,
          'Monthly Fee': course.fee,
          'Duration': course.duration_months,
          [batchTerm]: '-',
          'Timing': '-',
          'Start Date': '-',
          'End Date': '-'
        });
      }
    });

    exportToExcel(exportData, `${terms}_Export_${new Date().toISOString().split('T')[0]}`);
  };


  const handleEditClick = (course) => {
    setFormErrors({});
    setCourseForm({
      name: course.name,
      code: course.code,
      description: course.description || '',
      fee: course.fee,
      duration_months: course.duration_months || ''
    });
    setSelectedCourseId(course.id);
    setIsEditing(true);
    setShowCourseModal(true);
  };

  const handleAddClick = () => {
    setFormErrors({});
    setCourseForm({ name: '', code: '', description: '', fee: '', duration_months: '' });
    setIsEditing(false);
    setShowCourseModal(true);
  };

  const handleCourseSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setFormErrors({});

    try {
      const submissionData = { ...courseForm };
      if (isSchool) {
        submissionData.duration_months = 12; // Default to 12 months for schools
      }

      if (isEditing) {
        await CourseService.update(selectedCourseId, submissionData);
      } else {
        await CourseService.create(submissionData);
      }

      setShowCourseModal(false);
      loadCourses();
      setCourseForm({ name: '', code: '', description: '', fee: '', duration_months: '' });
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving course", error);
      if (error.isLimitError) return;

      if (error.response && error.response.data && error.response.data.errors) {
        setFormErrors(error.response.data.errors);
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to save course. Please checking inputs.'
        });
      }
    } finally {
      setSaving(false);
    }
  };

  // Helper to convert 24h time to 12h AM/PM
  const formatTimeAMPM = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const formattedDate = h % 12 || 12;
    return `${formattedDate}:${minutes} ${ampm}`;
  };

  const convertTo24Hour = (timeStr) => {
    if (!timeStr) return '';
    const [time, modifier] = timeStr.split(' ');
    let [hours, minutes] = time.split(':');

    let h = parseInt(hours, 10);
    if (modifier === 'PM' && h < 12) {
      h += 12;
    } else if (modifier === 'AM' && h === 12) {
      h = 0;
    }

    // Ensure HH:mm format with leading zeros
    const pad = (n) => n.toString().padStart(2, '0');
    return `${pad(h)}:${pad(minutes)}`;
  };

  // The original handleBatchSubmit is replaced by the inline submit in the Modal.
  // This empty function is kept to avoid breaking references if any, though it should be removed.
  const handleBatchSubmit = async (e) => {
    e.preventDefault();
    // This function is now handled inline within the Batch Modal's form onSubmit.
    // This placeholder is left to satisfy the original structure, but its logic is moved.
  };

  const openBatchModal = (courseId) => {
    setSelectedCourseId(courseId);
    setEditingBatchId(null); // Reset editing state for new batch
    setBatchForm({
      name: '',
      start_date: '',
      end_date: '',
      start_time: '',
      end_time: '',
      course_id: ''
    });
    setBatchErrors({});
    setShowBatchModal(true);
  };

  const handleEditBatch = (batch) => {
    setEditingBatchId(batch.id);
    setSelectedCourseId(batch.course_id);

    // Parse timing string "10:00 AM - 12:00 PM" back to start_time and end_time
    let start_time = '';
    let end_time = '';
    if (batch.timing) {
      const parts = batch.timing.split(' - ');
      if (parts.length === 2) {
        start_time = convertTo24Hour(parts[0]);
        end_time = convertTo24Hour(parts[1]);
      }
    }

    setBatchForm({
      name: batch.name,
      start_date: batch.start_date,
      end_date: batch.end_date,
      start_time: start_time,
      end_time: end_time,
      course_id: batch.course_id
    });
    setBatchErrors({});
    setShowBatchModal(true);
  };

  const handleDeleteCourse = async (id, name) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete ${name}. This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await CourseService.delete(id);
          Swal.fire(
            'Deleted!',
            'Course has been deleted.',
            'success'
          );
          loadCourses();
        } catch (error) {
          console.error("Error deleting course", error);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error.response?.data?.message || "Failed to delete course. It may have associated students."
          });
        }
      }
    });
  };

  const handleDeleteBatch = async (id, name) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete batch ${name}.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await CourseService.deleteBatch(id);
          Swal.fire(
            'Deleted!',
            'Batch has been deleted.',
            'success'
          );
          loadCourses();
        } catch (error) {
          console.error("Error deleting batch", error);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error.response?.data?.message || "Failed to delete batch."
          });
        }
      }
    });
  };

  return (
    <div className="container-fluid p-0">
      <div className="section-header mb-4 d-flex justify-content-between align-items-start">
        <div>
          <h2 className="fw-bold">{terms} Catalog</h2>
          <p className="text-muted">Manage academic programs and batches</p>
        </div>
        <div className="d-flex gap-2">
          {canExport && (
            <button className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 shadow-sm" onClick={handleExport}>
              <i className="bi bi-file-earmark-spreadsheet"></i> Export
            </button>
          )}
          {canManageCourses && (
            <button className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 shadow-sm" onClick={handleAddClick}>
              <i className="bi bi-plus-lg"></i> Add {term}
            </button>
          )}
        </div>
      </div>

      <div className="row g-4">
        {loading ? (
          <p className="text-center w-100 py-5 text-muted">Loading {terms.toLowerCase()}...</p>
        ) : courses.length > 0 ? (
          courses.map((course, index) => {
            const COLORS = [
              { bg: '#e0f2fe', text: '#0284c7' }, // Sky
              { bg: '#fef3c7', text: '#d97706' }, // Amber
              { bg: '#dcfce7', text: '#16a34a' }, // Green
              { bg: '#f3e8ff', text: '#9333ea' }, // Purple
              { bg: '#fee2e2', text: '#dc2626' }, // Red
            ];
            const theme = COLORS[index % COLORS.length];

            return (
              <div className="col-md-6 col-lg-4" key={course.id}>
                <div className="card-box h-100 shadow-sm border p-4 transition-all" style={{ transition: 'transform 0.2s, box-shadow 0.2s' }}>

                  {/* Header: Code & Edit */}
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-4 shadow-sm fw-bold px-3"
                      style={{ height: '52px', minWidth: '56px', width: 'fit-content', backgroundColor: theme.bg, color: theme.text, fontSize: '0.9rem', whiteSpace: 'nowrap' }}
                    >
                      {course.code}
                    </div>
                    {isAdmin && (
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-light btn-sm rounded-circle shadow-sm"
                          onClick={() => handleEditClick(course)}
                          title="Edit"
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                        <button
                          className="btn btn-light btn-sm rounded-circle shadow-sm text-danger"
                          onClick={() => handleDeleteCourse(course.id, course.name)}
                          title="Delete"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Body: Title & Desc */}
                  <div className="mb-4">
                    <h5 className="fw-bold text-dark mb-2">{course.name}</h5>
                    <p className="text-muted small mb-0 line-clamp-2" style={{ minHeight: '40px', lineHeight: '1.6' }}>
                      {course.description || `No description provided for this ${term.toLowerCase()}.`}
                    </p>
                  </div>

                  {/* Stats Row */}
                  <div className="d-flex align-items-center gap-3 mb-4 bg-light p-3 rounded-3 border-0">
                    {!isSchool && (
                      <>
                        <div className="d-flex align-items-center gap-2">
                          <i className="bi bi-clock-history text-primary"></i>
                          <div className="d-flex flex-column">
                            <span className="text-muted" style={{ fontSize: '0.7rem', fontWeight: 600 }}>DURATION</span>
                            <span className="fw-bold text-dark small">{course.duration_months} Months</span>
                          </div>
                        </div>
                        <div className="vr text-secondary opacity-25"></div>
                      </>
                    )}
                    <div className="d-flex align-items-center gap-2">
                      <i className="bi bi-tag text-success"></i>
                      <div className="d-flex flex-column">
                        <span className="text-muted" style={{ fontSize: '0.7rem', fontWeight: 600 }}>FEE</span>
                        <span className="fw-bold text-dark small">{getCurrencySymbol(settings?.currency)}{parseFloat(course.fee).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Footer: Batches */}
                  <div className="mt-auto pt-3 border-top border-light dashed">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <small className="fw-bold text-secondary " style={{ fontSize: '0.75rem' }}>{batchesTerm} ({course.batches?.length || 0})</small>
                      {isAdmin && (
                        <button
                          className="btn btn-sm btn-light border rounded-circle p-0 d-flex align-items-center justify-content-center text-primary"
                          style={{ width: '28px', height: '28px' }}
                          onClick={() => openBatchModal(course.id)}
                          title={`Add ${batchTerm}`}
                        >
                          <i className="bi bi-plus-lg text-primary" style={{ fontSize: '14px' }}></i>
                        </button>
                      )}
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {course.batches && course.batches.length > 0 ? (
                        course.batches.slice(0, 3).map(batch => (
                          <span key={batch.id} className="badge bg-white text-secondary border fw-normal shadow-sm px-2 py-1 d-inline-flex align-items-center gap-1" style={{ fontSize: '0.7rem' }}>
                            {batch.name}
                            {isAdmin && (
                              <>
                                <i
                                  className="bi bi-pencil-fill text-primary cursor-pointer content-hover ms-1"
                                  onClick={(e) => { e.stopPropagation(); handleEditBatch(batch); }}
                                  title={`Edit ${batchTerm}`}
                                  style={{ fontSize: '0.7rem' }}
                                ></i>
                                <i
                                  className="bi bi-x-circle-fill text-danger cursor-pointer content-hover"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteBatch(batch.id, batch.name); }}
                                  title={`Delete ${batchTerm}`}
                                  style={{ fontSize: '0.8rem' }}
                                ></i>
                              </>
                            )}
                          </span>
                        ))
                      ) : (
                        <small className="text-muted fst-italic" style={{ fontSize: '0.8rem' }}>No active {batchesTerm.toLowerCase()}</small>
                      )}
                      {course.batches && course.batches.length > 3 && (
                        <span className="badge bg-light text-muted border fw-normal" style={{ fontSize: '0.7rem' }}>+{course.batches.length - 3}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="col-12 text-center py-5">
            <div className="bg-light rounded-circle d-inline-flex p-4 mb-3">
              <i className="bi bi-inbox text-muted display-4"></i>
            </div>
            <p className="text-muted fw-semibold">No {terms.toLowerCase()} added yet.</p>
            <button className="btn btn-outline-primary btn-sm rounded-pill px-4 mt-2" onClick={handleAddClick}>
              Add Your First {term}
            </button>
          </div>
        )}
      </div>

      {/* Course Modal */}
      <Modal show={showCourseModal} onClose={() => setShowCourseModal(false)} title={`${isEditing ? 'Edit' : 'Add New'} ${term}`}>
        <form onSubmit={handleCourseSubmit}>
          <div className="mb-3">
            <label className="form-label small text-muted fw-bold">{term} Name</label>
            <input type="text" className={`form-control ${formErrors.name ? 'is-invalid' : ''}`} value={courseForm.name} onChange={(e) => setCourseForm({ ...courseForm, name: e.target.value })} required />
            {formErrors.name && <div className="invalid-feedback">{formErrors.name[0]}</div>}
          </div>
          <div className="mb-3">
            <label className="form-label small text-muted fw-bold">{term} Code</label>
            <input type="text" className={`form-control ${formErrors.code ? 'is-invalid' : ''}`} value={courseForm.code} onChange={(e) => setCourseForm({ ...courseForm, code: e.target.value })} required placeholder="e.g. CS101" />
            {formErrors.code && <div className="invalid-feedback">{formErrors.code[0]}</div>}
          </div>
          <div className="mb-3">
            <label className="form-label small text-muted fw-bold">Description</label>
            <textarea className="form-control" value={courseForm.description} onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })} rows="3" />
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label small text-muted fw-bold">Fee (per month)</label>
              <input type="number" className={`form-control ${formErrors.fee ? 'is-invalid' : ''}`} value={courseForm.fee} onChange={(e) => setCourseForm({ ...courseForm, fee: e.target.value })} required />
              {formErrors.fee && <div className="invalid-feedback">{formErrors.fee[0]}</div>}
            </div>
            {!isSchool && (
              <div className="col-md-6 mb-3">
                <label className="form-label small text-muted fw-bold">Duration (Months)</label>
                <input type="number" className={`form-control ${formErrors.duration_months ? 'is-invalid' : ''}`} value={courseForm.duration_months} onChange={(e) => setCourseForm({ ...courseForm, duration_months: e.target.value })} required />
                {formErrors.duration_months && <div className="invalid-feedback">{formErrors.duration_months[0]}</div>}
              </div>
            )}
          </div>
          <div className="d-flex justify-content-end gap-2 mt-4">
            <button type="button" className="btn btn-light px-4 rounded-pill" onClick={() => setShowCourseModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary px-4 rounded-pill d-flex align-items-center gap-2" disabled={saving}>
              {saving && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>}
              {saving ? 'Saving...' : (isEditing ? `Update ${term}` : `Save ${term}`)}
            </button>
          </div>
        </form>
      </Modal>

      {/* Batch Modal */}
      <Modal
        show={showBatchModal}
        onClose={() => setShowBatchModal(false)}
        title={editingBatchId ? `Edit ${batchTerm}` : `Add New ${batchTerm}`}
      >
        <form onSubmit={async (e) => {
          e.preventDefault();
          if (saving) return;

          // Validation
          const errors = {};
          if (!batchForm.name) errors.name = `${batchTerm} Name is required`;
          if (!batchForm.start_date) errors.start_date = 'Start Date is required';
          if (!batchForm.end_date) errors.end_date = 'End Date is required';
          if (!batchForm.start_time) errors.start_time = 'Start Time is required';
          if (!batchForm.end_time) errors.end_time = 'End Time is required';

          if (batchForm.start_date && batchForm.end_date && new Date(batchForm.end_date) <= new Date(batchForm.start_date)) {
            errors.end_date = 'End date must be after start date';
          }

          if (Object.keys(errors).length > 0) {
            setBatchErrors(errors);
            return;
          }

          setSaving(true);
          try {
            const timing = `${formatTimeAMPM(batchForm.start_time)} - ${formatTimeAMPM(batchForm.end_time)}`;
            const submitData = {
              ...batchForm,
              course_id: selectedCourseId,
              timing
            };

            if (editingBatchId) {
              await CourseService.updateBatch(editingBatchId, submitData);
              Swal.fire({
                icon: 'success',
                title: 'Success',
                text: `${batchTerm} updated successfully`,
                timer: 1500
              });
            } else {
              await CourseService.createBatch(submitData);
              Swal.fire({
                icon: 'success',
                title: 'Success',
                text: `${batchTerm} created successfully`,
                timer: 1500
              });
            }

            setShowBatchModal(false);
            setBatchForm({
              name: '',
              start_date: '',
              end_date: '',
              start_time: '',
              end_time: '',
              course_id: ''
            });
            setBatchErrors({});
            loadCourses();
          } catch (error) {
            console.error(`Error saving ${batchTerm.toLowerCase()}`, error);
            if (error.response && error.response.data && error.response.data.errors) {
              setBatchErrors(error.response.data.errors);
            } else {
              Swal.fire({
                icon: 'error',
                title: 'Error',
                text: `Failed to save ${batchTerm.toLowerCase()}.`
              });
            }
          } finally {
            setSaving(false);
          }
        }}>
          <div className="mb-3">
            <label className="form-label small text-muted fw-bold">{batchTerm} Name</label>
            <input type="text" className="form-control" value={batchForm.name} onChange={(e) => setBatchForm({ ...batchForm, name: e.target.value })} required placeholder={`e.g. ${batchTerm}-1`} />
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label small text-muted fw-bold">Start Date</label>
              <input type="date" className="form-control" value={batchForm.start_date} onChange={(e) => setBatchForm({ ...batchForm, start_date: e.target.value })} required />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label small text-muted fw-bold">End Date</label>
              <input
                type="date"
                className={`form-control ${batchErrors.end_date ? 'is-invalid' : ''}`}
                value={batchForm.end_date}
                min={batchForm.start_date}
                onChange={(e) => setBatchForm({ ...batchForm, end_date: e.target.value })}
                required
              />
              {batchErrors.end_date && <div className="invalid-feedback">{batchErrors.end_date[0]}</div>}
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label small text-muted fw-bold">Timing</label>
            <div className="input-group">
              <input
                type="time"
                className="form-control"
                value={batchForm.start_time}
                onChange={(e) => setBatchForm({ ...batchForm, start_time: e.target.value })}
                required
              />
              <span className="input-group-text bg-light text-muted">to</span>
              <input
                type="time"
                className="form-control"
                value={batchForm.end_time}
                onChange={(e) => setBatchForm({ ...batchForm, end_time: e.target.value })}
                required
              />
            </div>
            {batchErrors.timing && <div className="text-danger small mt-1">{batchErrors.timing[0]}</div>}
          </div>
          <div className="d-flex justify-content-end gap-2 mt-4">
            <button type="button" className="btn btn-light px-4 rounded-pill" onClick={() => setShowBatchModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary px-4 rounded-pill d-flex align-items-center gap-2" disabled={saving}>
              {saving && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>}
              {saving ? 'Saving...' : `Save ${batchTerm}`}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default CourseList;
